import { Button } from "@/components/catalyst/button";
import { Avatar } from "@/components/catalyst/avatar";
import { getInitials } from "@/lib/Utils/getInitials";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import {
  PlusIcon,
  StarIcon,
  TrashIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslations } from "use-intl";
import {
  useGqlMutation,
  useInfiniteGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  ProDriverLicenseType,
  SortOrder,
  WorkOrderJobOpeningOfferStatus,
} from "@/gql/graphql";
import {
  JobOpeningOfferItem,
  workOrderJobOpeningOfferListQuery,
} from "@/app/ProDrivers/WorkOrders/GraphQL/jobOpeningOfferListQuery";
import {
  deleteWorkOrderJobOpeningOfferMutation,
  updateWorkOrderJobOpeningOfferMutation,
} from "@/app/ProDrivers/WorkOrders/GraphQL/jobOpeningOfferMutations";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { CreateWorkOrderAssignmentDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/CreateWorkOrderAssignmentDialog";
import { useOpenProDriverDrawer } from "@/app/ProDrivers/Drivers/Components/Drawer/useProDriverDrawer";

const STATUS_DOT: Record<WorkOrderJobOpeningOfferStatus, string> = {
  [WorkOrderJobOpeningOfferStatus.Confirmed]: "bg-green-500",
  [WorkOrderJobOpeningOfferStatus.DriverAccepted]: "bg-yellow-500",
  [WorkOrderJobOpeningOfferStatus.Pending]: "bg-gray-400",
  [WorkOrderJobOpeningOfferStatus.Declined]: "bg-red-500",
};

const KANBAN_COLUMNS = [
  WorkOrderJobOpeningOfferStatus.Pending,
  WorkOrderJobOpeningOfferStatus.DriverAccepted,
  WorkOrderJobOpeningOfferStatus.Confirmed,
  WorkOrderJobOpeningOfferStatus.Declined,
];

const COLUMN_LIMIT = 10;

function useColumnOffers(
  jobOpeningId: string,
  status: WorkOrderJobOpeningOfferStatus,
) {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteGqlQuery(
      workOrderJobOpeningOfferListQuery,
      {
        job_opening_id: jobOpeningId,
        status: [status],
        first: COLUMN_LIMIT,
        orderByStatus: SortOrder.Asc,
      },
      {
        initialPageParam: { page: 1 },
        getNextPageParam: (lastPage) => {
          const info = lastPage.workOrderJobOpeningOffers.paginatorInfo;
          return info.hasMorePages ? { page: info.currentPage + 1 } : undefined;
        },
      },
    );

  return {
    offers:
      data?.pages.flatMap((page) => page.workOrderJobOpeningOffers.data) ?? [],
    total: data?.pages[0]?.workOrderJobOpeningOffers.paginatorInfo.total ?? 0,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}

function useAssignOffer() {
  const { mutateAsync, isPending } = useGqlMutation(
    updateWorkOrderJobOpeningOfferMutation,
  );
  const { openAsPromise: openAssignmentDialog } = useDialog(
    CreateWorkOrderAssignmentDialog,
  );

  const assign = async (offer: JobOpeningOfferItem) => {
    const result = await openAssignmentDialog({
      workOrderId: offer.jobOpening.workOrder.id,
      initialValues: {
        proDriverId: offer.proDriver.id,
        pickupDate: offer.jobOpening.depart_at,
        deliveryDate: offer.jobOpening.deliver_at,
        driverNotes: offer.jobOpening.description,
      },
    });

    if (result) {
      await mutateAsync({
        id: offer.id,
        input: { status: WorkOrderJobOpeningOfferStatus.Confirmed },
      });
    }
  };

  return { assign, isPending };
}

function useDeleteOffer() {
  const { open: openConfirmDelete } = useConfirmDeleteDialog();
  const { mutateAsync } = useGqlMutation(
    deleteWorkOrderJobOpeningOfferMutation,
  );

  const remove = (offer: JobOpeningOfferItem) => {
    openConfirmDelete({
      title: "Remove offer",
      message: `Are you sure you want to remove the offer for ${offer.proDriver.name}?`,
      buttonText: "Remove",
      onDelete: async () => {
        await mutateAsync({ id: offer.id });
        toast.success("Offer removed");
      },
    });
  };

  return { remove };
}

function DeleteOfferButton({ offer }: { offer: JobOpeningOfferItem }) {
  const { remove } = useDeleteOffer();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => remove(offer)}
          className="rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent>Remove offer</TooltipContent>
    </Tooltip>
  );
}

function DriverMeta({ offer }: { offer: JobOpeningOfferItem }) {
  const tLicense = useTranslations("proDriver.licenseType");
  const driver = offer.proDriver;

  return (
    <span className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
      {driver.rating ? (
        <span className="flex items-center gap-0.5">
          <StarIcon className="h-3 w-3 text-yellow-400" />
          {driver.rating.toFixed(1)}
        </span>
      ) : (
        <span className="text-gray-400">No rating</span>
      )}
      <span>·</span>
      <span>{driver.numberOfCompletedAssignments ?? 0} drives</span>
      {driver.license_type ? (
        <>
          <span>·</span>
          <span>
            {driver.license_type === ProDriverLicenseType.Heavy
              ? tLicense(ProDriverLicenseType.Heavy)
              : tLicense(ProDriverLicenseType.Standard)}
          </span>
        </>
      ) : null}
    </span>
  );
}

function AssignButton({ offer }: { offer: JobOpeningOfferItem }) {
  const t = useTranslations("workOrder.jobOpenings.offerActions");
  const { assign, isPending } = useAssignOffer();

  if (offer.status !== WorkOrderJobOpeningOfferStatus.DriverAccepted) {
    return null;
  }

  return (
    <Button
      color="green"
      className="mt-2 w-full"
      onClick={() => assign(offer)}
      disabled={isPending}
    >
      {t("assign")}
    </Button>
  );
}

function OfferCard({ offer }: { offer: JobOpeningOfferItem }) {
  const openDriverDrawer = useOpenProDriverDrawer();

  return (
    <div className="rounded-md border border-gray-200 bg-white p-2 shadow-sm">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => openDriverDrawer(offer.proDriver.id)}
          className="group flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <Avatar
            className="size-7"
            initials={getInitials(offer.proDriver.name)}
          />
          <span className="truncate text-sm font-medium text-gray-900 group-hover:text-blue-600 group-hover:underline">
            {offer.proDriver.name}
          </span>
        </button>
        <DeleteOfferButton offer={offer} />
      </div>
      <div className="mt-1.5">
        <DriverMeta offer={offer} />
      </div>
      <AssignButton offer={offer} />
    </div>
  );
}

export function OffersKanban({
  jobOpeningId,
  offersCount,
  onInvite,
}: {
  jobOpeningId: string;
  offersCount: number;
  onInvite?: () => void;
}) {
  const t = useTranslations("workOrder.jobOpenings");

  if (offersCount === 0) {
    return (
      <div className="flex flex-col items-center gap-1 py-6">
        <UsersIcon className="h-5 w-5 text-gray-400" />
        <p className="text-sm text-gray-500">{t("offersTable.noDrivers")}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {KANBAN_COLUMNS.map((status) => (
        <KanbanColumn
          key={status}
          jobOpeningId={jobOpeningId}
          status={status}
          onInvite={onInvite}
        />
      ))}
    </div>
  );
}

function KanbanColumn({
  jobOpeningId,
  status,
  onInvite,
}: {
  jobOpeningId: string;
  status: WorkOrderJobOpeningOfferStatus;
  onInvite?: () => void;
}) {
  const t = useTranslations("workOrder.jobOpenings");
  const {
    offers,
    total,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useColumnOffers(jobOpeningId, status);

  return (
    <div className="rounded-md bg-gray-50 p-2">
      <div className="mb-2 flex items-center gap-1.5 px-1 text-xs font-medium text-gray-600">
        <span className={`size-2 rounded-full ${STATUS_DOT[status]}`} />
        {t(`offerStatus.${status}`)}
        <span className="text-gray-400">{total}</span>
        {status === WorkOrderJobOpeningOfferStatus.Pending && onInvite ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onInvite}
                className="ml-auto rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t("sendToDrivers.button")}</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <div className="space-y-2">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Spinner className="size-4" />
          </div>
        ) : null}
        {offers.map((offer) => (
          <OfferCard key={offer.id} offer={offer} />
        ))}
        {!isLoading && total === 0 ? (
          <p className="px-1 py-2 text-xs text-gray-400">—</p>
        ) : null}
        {hasNextPage ? (
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="w-full rounded p-1 text-xs font-medium text-gray-500 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-50"
          >
            {isFetchingNextPage
              ? t("offersTable.loading")
              : t("offersTable.showMore", { count: total - offers.length })}
          </button>
        ) : null}
      </div>
    </div>
  );
}
